import { Layout, Spin } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/nb";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";

// Contexts
import { isBlMemberUser, isCompanyUser, useLoginContext } from "contexts/LoginContext/LoginContext";
import { ThemeContext, ThemeMode } from "contexts/theme-context";

// Components
import AdminFooter from "components/FooterAdmin/FooterAdmin";
import Navbar, { NAVBAR_HEIGHT } from "components/Nav/Navbar";
import AdminNavbar from "components/NavAdmin/AdminNavbar";
import ScrollToTop from "components/ScrollToTop/ScrollToTop";
import { NavMenu as AdminNavMenu, NAVMENU_WIDTH } from "components/NavAdmin/NavMenu";
import { NavMenu as CompanyNavMenu } from "containers/Company/CompanyProfile/Menu/NavMenu";
import { Footer as MainFooter } from "containers/Footer/Footer";
import useViewport from "components/UseViewport/UseViewport";

// Containers
import NotFound404 from "components/ErrorPage/NotFound404/NotFound404";
import AboutUs from "containers/AboutUs/AboutUs";
import AdminPage from "containers/Admin/AdminPage";
import Casebank from "containers/Casebank/Casebank";
import CompanyProfilePage from "containers/Company/CompanyProfile/CompanyProfile";
import EventBooking from "containers/Company/EventBooking/EventBooking";
import EventBookingInfo from "containers/Company/EventBookingInfo/EventBooking";
import ContactUs from "containers/ContactUs/ContactUs";
import CreateCompanyProfile from "containers/CreateCompanyProfile/CreateCompanyProfile";
import CreateStudentProfile from "containers/CreateStudentProfile/CreateStudentProfile";
import Events from "containers/Events/Events";
import FrontPage from "containers/FrontPage/FrontPage";
import JobPostings from "containers/JobPostings/JobPostings";
import PrivacyPolicy from "containers/PrivacyPolicy/PrivacyPolicy";
import Publications from "containers/Publications/Publications";
import SingleArticle from "containers/SingleArticle/SingleArticle";
import SingleCompany from "containers/SingleCompany/SingleCompany";
import SingleEventPage from "containers/SingleEventPage/SingleEventPage";
import SingleJobPosting from "containers/SingleJobPosting/SingleJobPosting";
import StudentProfilePage from "containers/StudentProfilePage/StudentProfilePage";
import UserTerms from "containers/UserTerms/UserTerms";
import AppKD from "kd/AppKD";
import UserResetPassword from "containers/UserResetPassword/UserResetPassword";

// Error Pages
import Forbidden403NotBL from "components/ErrorPage/Forbidden403/Forbidden403NotBL";
import Forbidden403NotCompany from "components/ErrorPage/Forbidden403/Forbidden403NotCompany";
import Forbidden403NotStudent from "components/ErrorPage/Forbidden403/Forbidden403NotStudent";

import "./App.less";

dayjs.locale("nb");

const { Header, Footer, Sider, Content } = Layout;

function App() {
  const isBrowserDefaultDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const { width } = useViewport();

  const [firstPath, setFirstPath] = useState(window.location.pathname.split("/")[1]);
  const [secondPath, setSecondPath] = useState(window.location.pathname.split("/")[2]);
  const { currentUser, isLoadingCurrentUser } = useLoginContext();
  const isBlMember = isBlMemberUser(currentUser);
  const isCompany = isCompanyUser(currentUser);

  const defaultTheme = useMemo(() => {
    const browserDefault = isBrowserDefaultDark ? "dark" : "light";
    const localStorageTheme = localStorage.getItem("theme");
    if (firstPath === "kd") {
      return "kd";
    }
    if (localStorageTheme !== "dark" && localStorageTheme !== "light") {
      localStorage.removeItem("theme");
      return browserDefault;
    }
    return localStorageTheme || browserDefault;
  }, [isBrowserDefaultDark, firstPath]);

  const [theme, _setTheme] = useState<ThemeMode>(defaultTheme);

  const setTheme = useCallback((newTheme: ThemeMode, storePreference = false) => {
    if (storePreference) {
      localStorage.setItem("theme", newTheme);
    }
    _setTheme(newTheme);
  }, []);

  useEffect(
    function setDefaultTheme() {
      setTheme(defaultTheme);
    },
    [defaultTheme, setTheme]
  );
  document.body.setAttribute("data-theme", theme);

  const checkUrl = () => {
    const paths = window.location.pathname.split("/");
    setFirstPath(paths[1] || "");
    setSecondPath(paths[2] || "");
  };

  useEffect(() => {
    checkUrl();
    window.addEventListener("popstate", checkUrl);
    window.addEventListener("pushState", checkUrl);
    window.addEventListener("click", checkUrl);

    return () => {
      window.removeEventListener("popstate", checkUrl);
      window.removeEventListener("pushState", checkUrl);
      window.removeEventListener("click", checkUrl);
    };
  }, []);

  const loadingComponent = (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      <Spin size="large" />
    </div>
  );

  return (
    <Router>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        <Layout>
          {firstPath === "admin" && isBlMember && width >= 768 && (
            <Sider width={NAVMENU_WIDTH} style={{ background: "#fff" }}>
              <AdminNavMenu />
            </Sider>
          )}
          <Layout style={{ padding: 0, minHeight: "100vh", background: "var(--neutral-alt)" }}>
            <Header style={{ position: "absolute", background: "transparent", padding: 0, inset: 0 }}>
              {firstPath === "kd" ? null : firstPath === "admin" && isBlMember ? <AdminNavbar /> : <Navbar />}
            </Header>
            {firstPath === "company" && secondPath === "company-profile" && isCompany && width >= 768 && (
              <Sider
                width={270}
                style={{
                  overflow: "auto",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  paddingTop: 50,
                  background: "var(--neutral)",
                  boxShadow: "1px 0px 5px rgba(0, 0, 0, 0.1)",
                }}
              >
                <CompanyNavMenu company={currentUser} />
              </Sider>
            )}
            <Content
              style={{
                margin: 0,
                paddingTop: firstPath === "" ? "0px" : `${NAVBAR_HEIGHT}px`,
                paddingBottom: "48px",
                minHeight: 280,
              }}
            >
              <ScrollToTop />
              <Switch>
                {firstPath === "admin" && !isBlMember && !isLoadingCurrentUser && (
                  <Route component={Forbidden403NotBL} />
                )}
                {firstPath === "company" && !isCompany && !isLoadingCurrentUser && (
                  <Route component={Forbidden403NotCompany} />
                )}
                {firstPath === "student-profile" && (!currentUser || isCompany) && !isLoadingCurrentUser && (
                  <Route component={Forbidden403NotStudent} />
                )}

                {/* Admin Routes */}
                <Route path="/admin/">{isLoadingCurrentUser ? loadingComponent : <AdminPage />}</Route>

                {/* Public Routes */}
                <Route exact path="/" component={FrontPage} />
                <Route path="/events/:id/" component={SingleEventPage} />
                <Route path="/user/resetpassword/" component={UserResetPassword} />
                <Route path="/events/" component={Events} />
                <Route path="/jobs/:id/" component={SingleJobPosting} />
                <Route path="/jobs/" component={JobPostings} />
                <Route path="/companies/:id/" component={SingleCompany} />
                <Route path="/casebank/" component={Casebank} />
                <Route path="/publications/" component={Publications} />
                <Route path="/articles/:id/" component={SingleArticle} />
                <Route path="/about-us/" component={AboutUs} />
                <Route path="/contact-us/" component={ContactUs} />
                <Route path="/privacy-policy/" component={PrivacyPolicy} />
                <Route path="/user-terms/" component={UserTerms} />
                <Route path="/create-student-profile/" component={CreateStudentProfile} />
                <Route path="/create-company-profile/" component={CreateCompanyProfile} />
                <Route path="/student-profile/">
                  {isLoadingCurrentUser ? loadingComponent : <StudentProfilePage />}
                </Route>

                {/* Company Routes */}
                <Route path="/company/company-profile/">
                  {isLoadingCurrentUser ? loadingComponent : <CompanyProfilePage />}
                </Route>
                <Route path="/company/event-booking-info/" component={EventBookingInfo} />
                <Route path="/company/event-booking/" component={EventBooking} />

                {/* Temporary KD Setup */}
                <Route path="/kd/" component={AppKD} />

                {/* Fallback Route */}
                <Route component={NotFound404} />
              </Switch>
            </Content>
          </Layout>

          {/* Footer */}
          {!(firstPath === "admin" && isBlMember) && (
            <Footer style={{ padding: 0 }}>{firstPath === "kd" ? null : <MainFooter />}</Footer>
          )}
        </Layout>

        {/* Footer admin */}
        {firstPath === "admin" && isBlMember && (
          <Footer style={{ padding: 0 }}>
            <AdminFooter />
          </Footer>
        )}
      </ThemeContext.Provider>
    </Router>
  );
}

export default App;
